'use client'

import { useRef } from 'react'
import { type Control } from 'react-hook-form'

import { FiltersMobileSheetShell } from '../../components/FiltersMobileSheet/FiltersMobileSheetShell'
import { GatewayListMobileOptionFilterSection } from '../../components/FiltersMobileSheet/GatewayListMobileOptionFilterSection'
import { GatewayListMobileTimeRangeFilterSection } from '../../components/FiltersMobileSheet/GatewayListMobileTimeRangeFilterSection'
import {
  type GatewayListMobileFiltersApplyValues,
  type GatewayListMobileFiltersControlValues,
  useGatewayListMobileFilters,
} from '../hooks/useGatewayListMobileFilters'
import type { GatewayListFilterTraitsSource } from '../types/gatewayListFilters.types'

export type GatewayListMobileFiltersSheetProps<TFieldValues extends GatewayListMobileFiltersControlValues> = {
  isOpen: boolean
  onClose: () => void
  control: Control<TFieldValues>
  filterSources: GatewayListFilterTraitsSource[]
  onApplyFilters: (values: GatewayListMobileFiltersApplyValues) => void
  menuItemTestIdPrefix?: string
  sheetRootTestId?: string
  moduleRowLabel?: string
  environmentRowLabel?: string
}

export function GatewayListMobileFiltersSheet<TFieldValues extends GatewayListMobileFiltersControlValues>({
  isOpen,
  onClose,
  control,
  filterSources,
  onApplyFilters,
  menuItemTestIdPrefix,
  sheetRootTestId,
  moduleRowLabel = 'Modules',
  environmentRowLabel = 'Project keys',
}: GatewayListMobileFiltersSheetProps<TFieldValues>) {
  const customGroupRef = useRef<HTMLDivElement | null>(null)
  const {
    canApplyCustomRange,
    environmentSelectOptions,
    environmentTriggerLabel,
    feedOptions,
    feedTriggerLabel,
    handleApply,
    handleReset,
    handleSelectTimeRange,
    menuItems,
    openMenu,
    pendingCustomRange,
    pendingFeed,
    pendingEnvironment,
    pendingRuntime,
    pendingStatus,
    pendingTimeRange,
    runtimeOptions,
    runtimeTriggerLabel,
    setEndDate,
    setEndTime,
    setOpenMenu,
    setPendingFeed,
    setPendingEnvironment,
    setPendingRuntime,
    setPendingStatus,
    setStartDate,
    setStartTime,
    showCustom,
    statusOptions,
    statusTriggerLabel,
    timeRangeLabels,
    timeRangeTriggerLabel,
  } = useGatewayListMobileFilters({
    isOpen,
    control,
    customGroupRef,
    filterSources,
    onApplyFilters,
    onClose,
  })

  const optionTestIdPrefix = menuItemTestIdPrefix ?? 'gateway-list-filter-sheet-option'

  return (
    <FiltersMobileSheetShell
      isOpen={isOpen}
      onClose={onClose}
      title="Filters"
      resetLabel="Reset"
      applyLabel="Apply"
      applyDisabled={!canApplyCustomRange}
      onReset={handleReset}
      onApply={handleApply}
    >
      <GatewayListMobileTimeRangeFilterSection
        isOpen={isOpen}
        customGroupRef={customGroupRef}
        timeRangeLabels={timeRangeLabels}
        timeRangeTriggerLabel={timeRangeTriggerLabel}
        openMenu={openMenu === 'timeRange'}
        onMenuOpenChange={(open) => setOpenMenu(open ? 'timeRange' : null)}
        menuItems={menuItems}
        pendingTimeRange={pendingTimeRange}
        onSelectTimeRange={handleSelectTimeRange}
        showCustom={showCustom}
        pendingCustomRange={pendingCustomRange}
        onStartDateChange={setStartDate}
        onStartTimeChange={setStartTime}
        onEndDateChange={setEndDate}
        onEndTimeChange={setEndTime}
        menuItemTestIdPrefix={`${optionTestIdPrefix}-time`}
        sheetRootTestId={sheetRootTestId}
      />

      <GatewayListMobileOptionFilterSection
        rowLabel="Status"
        displayValue={statusTriggerLabel}
        menuOpen={openMenu === 'status'}
        onMenuOpenChange={(open) => setOpenMenu(open ? 'status' : null)}
        options={statusOptions}
        selectedId={pendingStatus}
        onSelect={(id) => {
          setPendingStatus(id)
          setOpenMenu(null)
        }}
        menuItemTestIdPrefix={optionTestIdPrefix}
      />

      <GatewayListMobileOptionFilterSection
        rowLabel="Runtime"
        displayValue={runtimeTriggerLabel}
        menuOpen={openMenu === 'runtime'}
        onMenuOpenChange={(open) => setOpenMenu(open ? 'runtime' : null)}
        options={runtimeOptions}
        selectedId={pendingRuntime}
        onSelect={(id) => {
          setPendingRuntime(id)
          setOpenMenu(null)
        }}
        menuItemTestIdPrefix={optionTestIdPrefix}
      />

      <GatewayListMobileOptionFilterSection
        rowLabel={moduleRowLabel}
        displayValue={feedTriggerLabel}
        menuOpen={openMenu === 'feed'}
        onMenuOpenChange={(open) => setOpenMenu(open ? 'feed' : null)}
        options={feedOptions}
        selectedId={pendingFeed}
        onSelect={(id) => {
          setPendingFeed(id)
          setOpenMenu(null)
        }}
        menuItemTestIdPrefix={optionTestIdPrefix}
      />

      <GatewayListMobileOptionFilterSection
        rowLabel={environmentRowLabel}
        displayValue={environmentTriggerLabel}
        menuOpen={openMenu === 'environment'}
        onMenuOpenChange={(open) => setOpenMenu(open ? 'environment' : null)}
        options={environmentSelectOptions}
        selectedId={pendingEnvironment}
        onSelect={(id) => {
          setPendingEnvironment(id)
          setOpenMenu(null)
        }}
        menuItemTestIdPrefix={optionTestIdPrefix}
      />
    </FiltersMobileSheetShell>
  )
}
